/**
 * Lazy-loads images/videos/iframes deferred by snippets/lazyify.liquid.
 * Deferred elements carry data-src (img/iframe), data-srcset (img), or data-lazy-video (video).
 * They load ~400px before entering the viewport, OR all at once on first user interaction.
 */

const SELECTOR =
  'img[data-src]:not([data-lazy-bound]), iframe[data-src]:not([data-lazy-bound]), video[data-lazy-video]:not([data-lazy-bound])';
const INTERACTION_EVENTS = ['scroll', 'pointerdown', 'touchstart', 'keydown', 'wheel'];

/** @type {IntersectionObserver | null} */
let observer = null;
/** @type {Set<Element>} */
const bound = new Set();
let interactionArmed = false;
let interactionFired = false;
/** @type {MutationObserver | null} */
let domObserver = null;

/** @param {Element} el */
function loadEl(el) {
  if (!bound.has(el)) return;
  bound.delete(el);
  if (observer) observer.unobserve(el);

  if (el instanceof HTMLVideoElement) {
    const poster = el.getAttribute('data-poster');
    if (poster) {
      el.setAttribute('poster', poster);
      el.removeAttribute('data-poster');
    }
    el.querySelectorAll('source[data-src]').forEach((source) => {
      const s = source.getAttribute('data-src');
      if (s) source.setAttribute('src', s);
      source.removeAttribute('data-src');
    });
    el.removeAttribute('data-lazy-video');
    el.load();
    if (el.muted || el.hasAttribute('autoplay')) {
      const playPromise = el.play();
      if (playPromise && typeof playPromise.catch === 'function') playPromise.catch(() => {});
    }
    el.classList.add('is-loaded');
    return;
  }

  if (el instanceof HTMLIFrameElement) {
    const src = el.getAttribute('data-src');
    if (src) {
      el.setAttribute('src', src);
      el.removeAttribute('data-src');
    }
    el.classList.add('is-loaded');
    return;
  }

  if (el instanceof HTMLImageElement) {
    const markLoaded = () => el.classList.add('is-loaded');
    el.addEventListener('load', markLoaded, { once: true });
    el.addEventListener('error', markLoaded, { once: true });
    const srcset = el.getAttribute('data-srcset');
    if (srcset) {
      el.setAttribute('srcset', srcset);
      el.removeAttribute('data-srcset');
    }
    const src = el.getAttribute('data-src');
    if (src) {
      el.setAttribute('src', src);
      el.removeAttribute('data-src');
    }
    if (el.complete) markLoaded();
  }
}

/**
 * Bind one deferred element: mark it, then either observe it (defer) or load it
 * immediately when the first-interaction gate has already passed or IO is unsupported.
 * Idempotent via the data-lazy-bound attribute + the `bound` Set.
 * @param {Element} el
 */
function bindEl(el) {
  if (el.hasAttribute('data-lazy-bound') || bound.has(el)) return;
  el.setAttribute('data-lazy-bound', '');
  el.classList.add('lazy-bound');
  bound.add(el);
  if (interactionFired || !observer) {
    loadEl(el);
  } else {
    observer.observe(el);
  }
}

function loadAll() {
  interactionFired = true;
  for (const el of [...bound]) loadEl(el);
  disarmInteraction();
}

function onInteraction() {
  loadAll();
}

function armInteraction() {
  if (interactionArmed || interactionFired) return;
  interactionArmed = true;
  for (const evt of INTERACTION_EVENTS) {
    window.addEventListener(evt, onInteraction, { once: true, passive: true });
  }
}

function disarmInteraction() {
  if (!interactionArmed) return;
  for (const evt of INTERACTION_EVENTS) {
    window.removeEventListener(evt, onInteraction);
  }
  interactionArmed = false;
}

function ensureObserver() {
  if (observer) return;
  observer = new IntersectionObserver(
    (entries) => {
      for (const entry of entries) {
        if (entry.isIntersecting) loadEl(entry.target);
      }
    },
    { rootMargin: '400px 0px', threshold: 0 }
  );
}

/**
 * Scan `root` for deferred media and bind it.
 * @param {ParentNode | EventTarget | null} [root]
 */
export function initLazyLoad(root) {
  const scope =
    root && typeof (/** @type {any} */ (root).querySelectorAll) === 'function'
      ? /** @type {ParentNode} */ (root)
      : document;
  const els = scope.querySelectorAll(SELECTOR);
  if (els.length === 0) return;

  if ('IntersectionObserver' in window) {
    ensureObserver();
    armInteraction();
  }
  for (const el of els) bindEl(el);
}

/** @param {MutationRecord[]} mutations */
function handleMutations(mutations) {
  for (const mutation of mutations) {
    for (const node of mutation.addedNodes) {
      if (!(node instanceof Element)) continue;
      if (node.matches(SELECTOR)) bindEl(node);
      const nested = node.querySelectorAll(SELECTOR);
      for (const el of nested) bindEl(el);
    }
  }
}

function startDomObserver() {
  if (domObserver || typeof MutationObserver === 'undefined') return;
  const target = document.body || document.documentElement;
  if (!target) return;
  domObserver = new MutationObserver(handleMutations);
  domObserver.observe(target, { childList: true, subtree: true });
}

function boot() {
  initLazyLoad(document);
  startDomObserver();
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', boot);
} else {
  boot();
}

document.addEventListener('shopify:section:load', (event) => initLazyLoad(event.target));
document.addEventListener('shopify:block:load', (event) => initLazyLoad(event.target));
